import {
    createGuests,
    createShowName,
    createShowTime,
    createUpNext,
} from "../../utils/ScheduleRenderHelper";

import LiveNow from "../live-now/LiveNow";

import React from "react";
import PropTypes from "prop-types";
import Navigation from "../../utils/Navigation";
import GTMTracker from "../../utils/tracking/GTMTracker";
import { fullScheduleComponentProps } from "../../constants/ComponentProps";
import { getLiveStreamUrl, getShowScheduleUrl } from "../../utils/Route";

function trackAndNavigate(event, trackingEventLabel) {
    GTMTracker.trackEngagement({
        bvpEventAction: "click",
        bvpEventLabel: trackingEventLabel,
    });

    Navigation.delayNavigateTo(event);
}

const WatchNow = ({ children, currentChannelId, onClick }) => (
    <div className="live-schedule__watch-now">
        <a
            {...{ onClick }}
            className="live-schedule__watch-now-link"
            href={getLiveStreamUrl(currentChannelId)}
        >
            {children}
        </a>
    </div>
);

const MaybeLink = ({
    children,
    currentChannelId,
    shouldLinkLiveNowItem,
    onClick,
}) => {
    if (shouldLinkLiveNowItem) {
        return (
            <a
                {...{ onClick }}
                className="live-schedule__item-link"
                href={getLiveStreamUrl(currentChannelId)}
            >
                {children}
            </a>
        );
    }
    return children;
};

const ScheduleItem = ({
    additionalClass,
    index,
    localizer,
    startTime,
    showTitle,
    guests,
    showDescription,
    shouldShowDescription,
    children,
}) => (
    <div className={`live-schedule__item ${additionalClass}`}>
        {createUpNext("live-schedule", index, localizer)}
        {children}
        {createShowTime("live-schedule", startTime)}
        {createShowName("h2", "live-schedule", localizer, showTitle)}
        {shouldShowDescription && (
            <span
                className="live-schedule__item-description"
                dangerouslySetInnerHTML={{ __html: showDescription }}
            />
        )}
        {createGuests("live-schedule", guests || [])}
    </div>
);

const ScheduleItems = ({
    schedules,
    currentChannelId,
    isActiveChannel,
    localizer,
    onClick,
    channelsByChannelId,
}) => (
    <ul className="live-schedule__items">
        {schedules.map(
            ({ startTime, showTitle, guests, showDescription }, index) => {
                const isLiveNow = index === 0;
                const itemOptions = {
                    index,
                    localizer,
                    startTime,
                    showTitle,
                    guests,
                    showDescription,
                    shouldShowDescription: isLiveNow && showDescription,
                    additionalClass: isLiveNow
                        ? "live-schedule__item--current"
                        : "",
                };
                const liveNowOptions = {
                    localizer,
                    channelsByChannelId,
                    prefix: "live-schedule",
                    showStreamName: false,
                    channelId: currentChannelId,
                    isDisplayed: isLiveNow,
                };

                return (
                    <li
                        className="live-schedule__item-container"
                        key={`schedule-${index}`}
                    >
                        <MaybeLink
                            {...{ currentChannelId, onClick }}
                            shouldLinkLiveNowItem={
                                isLiveNow && !isActiveChannel
                            }
                        >
                            <ScheduleItem {...itemOptions}>
                                {isLiveNow && isActiveChannel && (
                                    <LiveNow {...liveNowOptions} />
                                )}
                            </ScheduleItem>
                        </MaybeLink>
                    </li>
                );
            }
        )}
    </ul>
);

const ChannelFinder = () => {
    return (
        <div className="live-schedule__channel-finder-container">
            <span className="live-schedule__channel-finder-title">
                BTV Channel Finder
            </span>
            <a
                className="live-schedule__channel-finder-link"
                href="https://bloomberg2023.viewerlink.tv/"
                target="_blank"
                rel="noopener noreferrer"
            >
                Watch BTV in your area
            </a>
        </div>
    );
};

export default class LiveSchedule extends React.Component {
    constructor(props) {
        super(props);
        this._handleLiveNowClick = this._handleLiveNowClick.bind(this);
        this._handleWatchNowClick = this._handleWatchNowClick.bind(this);
    }

    render() {
        const {
            isFetching,
            schedules,
            isActiveChannel,
            localizer,
            currentChannelId,
            showFullScheduleLink,
            scheduleLink,
            channelsByChannelId,
        } = this.props;

        if (!isFetching && schedules.length === 0) {
            return (
                <>
                    <div className="live-schedule--empty">
                        No schedule data available.
                    </div>
                    <ChannelFinder />
                </>
            );
        }

        return (
            <div className="live-schedule">
                <div className="live-schedule__container" tabIndex="0">
                    {!isActiveChannel && (
                        <WatchNow
                            {...{ currentChannelId }}
                            onClick={this._handleWatchNowClick}
                        >
                            {localizer("WATCH_NOW")}
                        </WatchNow>
                    )}
                    <ScheduleItems
                        {...{
                            channelsByChannelId,
                            schedules,
                            currentChannelId,
                            isActiveChannel,
                            localizer,
                        }}
                        onClick={this._handleLiveNowClick}
                    />
                    {showFullScheduleLink && (
                        <a
                            className="live-schedule__see-full-schedule"
                            href={getShowScheduleUrl(
                                scheduleLink || currentChannelId
                            )}
                        >
                            {localizer("SEE_FULL_SCHEDULE")}
                        </a>
                    )}
                    <ChannelFinder />
                </div>
            </div>
        );
    }

    _handleWatchNowClick(event) {
        const { eventPrefix, currentChannelId } = this.props;
        trackAndNavigate(
            event,
            `${eventPrefix}.schedule_watch_now_${currentChannelId}`
        );
    }

    _handleLiveNowClick(event) {
        const { eventPrefix, currentChannelId } = this.props;
        trackAndNavigate(
            event,
            `${eventPrefix}.schedule_live_now_${currentChannelId}`
        );
    }
}

LiveSchedule.defaultProps = {
    isFetching: false,
    schedules: [],
    eventPrefix: "",
    isActiveChannel: false,
    localizer: (input) => input,
};

LiveSchedule.propTypes = {
    eventPrefix: PropTypes.string,
    isFetching: PropTypes.bool,
    currentChannelId: PropTypes.string.isRequired,
    isActiveChannel: PropTypes.bool,
    schedules: fullScheduleComponentProps,
    localizer: PropTypes.func,
    showFullScheduleLink: PropTypes.bool,
    scheduleLink: PropTypes.string,
    channelsByChannelId: PropTypes.object.isRequired,
};
